import React from "react";
import { Link } from "react-router-dom";
import { MODULES } from "@/data/constants";
import { ModuleIcon } from "@/components/ModuleIcon";
import { ModuleTitle } from "@/components/ProgereText";
import { ConstructionBadge } from "@/components/ConstructionBadge";
import { useI18n } from "@/context/I18nContext";
import { ArrowUpRight, Crown } from "lucide-react";

function ModuleCard({ m, index }) {
  const { t } = useI18n();
  const isGold = m.color === "gold";
  const accent = isGold ? "#EAB308" : "#dc143c";
  const to = m.key === "support" ? "/support" : `/module/${m.key}`;
  return (
    <Link
      to={to}
      data-testid={`module-card-${m.key}`}
      className="group relative flex flex-col rounded-lg border border-white/10 bg-[#0a0a0a] p-7 overflow-hidden transition-colors duration-300 hover:border-white/25"
      style={{ "--accent": accent }}
    >
      {/* hover glow */}
      <div
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: `radial-gradient(400px circle at top right, ${accent}22, transparent 60%)` }}
      />
      <div className="relative flex items-start justify-between">
        <div
          className="flex h-13 w-13 items-center justify-center rounded-md border transition-colors duration-300"
          style={{ borderColor: `${accent}44`, backgroundColor: `${accent}12`, width: 52, height: 52 }}
        >
          <ModuleIcon name={m.icon} className="w-6 h-6" style={{ color: accent }} />
        </div>
        {m.premium && (
          <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: accent, backgroundColor: `${accent}18`, border: `1px solid ${accent}33` }}
          >
            <Crown className="w-3 h-3" />
            {m.premium}
          </span>
        )}
      </div>

      <h3 className="relative mt-6 text-xl font-bold"><ModuleTitle name={m.name} /></h3>
      <p className="relative mt-1 text-xs font-mono uppercase tracking-wider" style={{ color: accent }}>
        {t(`moduleData.${m.key}.tagline`)}
      </p>
      {m.construction && <ConstructionBadge className="relative mt-3 self-start" />}
      <p className="relative mt-3 text-sm text-white/55 leading-relaxed flex-1">
        {t(`moduleData.${m.key}.description`)}
      </p>

      {m.key === "support" ? (
        <div className="relative mt-6">
          <span
            data-testid="support-help-btn"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-transform duration-300 group-hover:-translate-y-0.5"
            style={{ color: "#fff", backgroundColor: `${accent}`, boxShadow: `0 8px 24px -8px ${accent}` }}
          >
            {t("modules.supportCta")}
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
      ) : (
        <div className="relative mt-6 flex items-center gap-2 text-sm font-medium text-white/70 group-hover:text-white transition-colors">
          {t("modules.discover")}
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      )}
    </Link>
  );
}

export function ModulesSection() {
  const { t } = useI18n();
  return (
    <section id="modules" className="relative py-24 lg:py-32" data-testid="modules-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#EAB308]">
            {t("modules.eyebrow")}
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
            {t("modules.titleA")} <span className="text-[#EAB308]">{t("modules.titleB")}</span>
          </h2>
          <p className="mt-4 text-white/60 text-lg">
            {t("modules.desc")}
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {MODULES.map((m, i) => (
            <ModuleCard key={m.key} m={m} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
